import React, { useState } from 'react';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogContent,
    IconButton,
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import CloseIcon from '@mui/icons-material/Close';
import plusBlackIcon from '../../../assets/icons/PlusBlack.svg';
import FAQs from '../settings/faqs.json';
import tutorialVideos from './help-hub-tutorials.json';

const FAQ_TOGGLE_ICON_SIZE = '33.131px';
// CETRA-815: tutorial videos are out of date, hide the section until content is refreshed
const SHOW_TUTORIAL_VIDEOS = false;

const HelpHub = () => {
    const [expandedFaq, setExpandedFaq] = useState(false);
    const [openVideo, setOpenVideo] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleFaqChange = (panel) => (_, isExpanded) => {
        setExpandedFaq(isExpanded ? panel : false);
    };

    const getYouTubeVideoId = (url) => {
        if (!url) return null;
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/);
        return match ? match[1] : null;
    };

    return (
        <Box
            sx={{
                backgroundColor: '#F0F0FC',
                minHeight: '100vh',
                px: { xs: '24px', md: '40px' },
                pt: { xs: '24px', md: '39px' },
                pb: { xs: '24px', md: '40px' },
            }}
        >
            <Box sx={{ maxWidth: '1360px', mx: 'auto' }}>
                <Typography
                    sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: { xs: '24px', md: '40px' },
                        lineHeight: { xs: '24px', md: '40px' },
                        letterSpacing: { xs: '-0.96px', md: '-1.6px' },
                        color: '#000453',
                        mb: { xs: '20px', md: '20px' },
                    }}
                >
                    Help hub
                </Typography>

                {SHOW_TUTORIAL_VIDEOS && (
                    <Box
                        sx={{
                            backgroundColor: '#FFFFFF',
                            borderRadius: { xs: '30px', md: '30px' },
                            p: { xs: '20px', md: '20px' },
                        }}
                    >
                        <Typography
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 600,
                                fontSize: { xs: '20px', md: '30px' },
                                lineHeight: { xs: '20px', md: '30px' },
                                letterSpacing: { xs: '-0.8px', md: '-1.2px' },
                                color: '#000453',
                                mb: { xs: '16px', md: '16px' },
                            }}
                        >
                            Tutorial videos
                        </Typography>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                gap: { xs: '14px', md: '20px' },
                            }}
                        >
                            {tutorialVideos.map((video) => {
                                const videoId = getYouTubeVideoId(video.youtubeUrl);
                                const thumbnailUrl = videoId
                                    ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
                                    : '';

                                return (
                                    <Box
                                        key={video.title}
                                        sx={{
                                            display: 'block',
                                            backgroundColor: '#F0F0FC',
                                            borderRadius: { xs: '10px', md: '20px' },
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                background: 'rgba(250, 150, 195, 0.3)',
                                                height: { xs: '167px', md: '348px' },
                                                borderTopLeftRadius: { xs: '4.8px', md: '10px' },
                                                borderTopRightRadius: { xs: '4.8px', md: '10px' },
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                position: 'relative',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {thumbnailUrl && (
                                                <Box
                                                    component="img"
                                                    src={thumbnailUrl}
                                                    alt={`${video.title} thumbnail`}
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                        objectPosition: 'center',
                                                    }}
                                                />
                                            )}
                                            <Box
                                                onClick={() => setOpenVideo(video)}
                                                sx={{
                                                    width: { xs: '45px', md: '94px' },
                                                    height: { xs: '45px', md: '94px' },
                                                    borderRadius: '50%',
                                                    backgroundColor: '#EA77B0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    position: 'relative',
                                                    zIndex: 1,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <PlayArrowRoundedIcon
                                                    sx={{
                                                        color: '#fff',
                                                        fontSize: { xs: 30, md: 56 },
                                                        ml: { xs: '3px', md: '6px' },
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontFamily: 'Inter, sans-serif',
                                                fontWeight: 600,
                                                fontSize: { xs: '18px', md: '26px' },
                                                lineHeight: { xs: '18px', md: '26px' },
                                                letterSpacing: { xs: '-0.36px', md: '-0.52px' },
                                                color: '#000453',
                                                p: { xs: '8px 8px 10px 8px', md: '20px 16px 16px 16px' },
                                            }}
                                        >
                                            {video.title}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}

                <Box
                    sx={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: '30px',
                        p: { xs: '20px', md: '25px' },
                        mt: { xs: '20px', md: '35px' },
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: '20px', md: '30px' },
                            lineHeight: { xs: '20px', md: '30px' },
                            letterSpacing: { xs: '-0.8px', md: '-1.2px' },
                            color: '#000453',
                            mb: { xs: '16px', md: '18px' },
                        }}
                    >
                        Frequently asked questions
                    </Typography>

                    {FAQs.map((faq, index) => {
                        const isExpanded = expandedFaq === index;

                        return (
                            <Accordion
                                key={faq.question}
                                expanded={isExpanded}
                                onChange={handleFaqChange(index)}
                                sx={{
                                    mb: 1.5,
                                    backgroundColor: '#F0F0FC',
                                    color: '#151515',
                                    boxShadow: 0,
                                    borderRadius: '12px !important',
                                    '&:before': {
                                        display: 'none',
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        isExpanded ? (
                                            <Box
                                                component="img"
                                                src={plusBlackIcon}
                                                alt="close icon"
                                                sx={{
                                                    width: FAQ_TOGGLE_ICON_SIZE,
                                                    height: FAQ_TOGGLE_ICON_SIZE,
                                                    display: 'block',
                                                    flexShrink: 0,
                                                    transform: 'rotate(45deg)',
                                                }}
                                            />
                                        ) : (
                                            <Box
                                                component="img"
                                                src={plusBlackIcon}
                                                alt="plus icon"
                                                sx={{
                                                    width: FAQ_TOGGLE_ICON_SIZE,
                                                    height: FAQ_TOGGLE_ICON_SIZE,
                                                    flexShrink: 0,
                                                }}
                                            />
                                        )
                                    }
                                    aria-controls={`help-hub-faq-panel-${index}-content`}
                                    id={`help-hub-faq-panel-${index}-header`}
                                    sx={{
                                        minHeight: { xs: '58px', md: '74px' },
                                        px: { xs: '14px', md: '18px' },
                                        '& .MuiAccordionSummary-expandIconWrapper': {
                                            width: FAQ_TOGGLE_ICON_SIZE,
                                            height: FAQ_TOGGLE_ICON_SIZE,
                                            minWidth: FAQ_TOGGLE_ICON_SIZE,
                                            minHeight: FAQ_TOGGLE_ICON_SIZE,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        },
                                        '& .MuiAccordionSummary-content': {
                                            margin: '0',
                                        },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter, sans-serif',
                                            fontWeight: 500,
                                            fontSize: { xs: '18px', md: '25.842px' },
                                            lineHeight: { xs: '20px', md: '39.758px' },
                                            letterSpacing: { xs: '-0.18px', md: '-0.212px' },
                                            color: '#151515',
                                        }}
                                    >
                                        {faq.question}
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails
                                    sx={{ px: { xs: '14px', md: '18px' }, pb: { xs: '14px', md: '18px' }, pt: 0 }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: 'Inter, sans-serif',
                                            fontWeight: 400,
                                            fontSize: { xs: '16px', md: '26.339px' },
                                            lineHeight: { xs: '26px', md: '39.758px' },
                                            letterSpacing: { xs: '-0.35px', md: '-0.636px' },
                                            color: '#747474',
                                        }}
                                    >
                                        {faq.answer}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Box>
            </Box>

            {openVideo && (
                <Dialog
                    open
                    onClose={() => setOpenVideo(null)}
                    maxWidth="lg"
                    fullWidth
                    fullScreen={isMobile}
                    sx={{
                        '& .MuiDialog-paper': {
                            borderRadius: isMobile ? 0 : '24px',
                            overflow: 'hidden',
                            backgroundColor: '#000',
                        },
                    }}
                >
                    <DialogContent sx={{ p: 0, m: 0, position: 'relative', backgroundColor: '#000' }}>
                        <IconButton
                            onClick={() => setOpenVideo(null)}
                            sx={{
                                position: 'absolute',
                                top: '14px',
                                right: '14px',
                                zIndex: 10,
                                width: '35px',
                                height: '35px',
                                background: '#ECECEC',
                                color: '#5E5E5E',
                                '&:hover': { background: '#E2E2E2' },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                        <Box
                            sx={{
                                width: '100%',
                                aspectRatio: '16 / 9',
                                maxHeight: isMobile ? '100dvh' : '80vh',
                            }}
                        >
                            <Box
                                component="iframe"
                                src={`https://www.youtube.com/embed/${getYouTubeVideoId(openVideo.youtubeUrl)}?autoplay=1`}
                                title={openVideo.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                allowFullScreen
                                sx={{
                                    border: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'block',
                                }}
                            />
                        </Box>
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    );
};

export default HelpHub;
